import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { SearchOptions, objectToUrlParams, TreetopPropertiesProvider } from 'shared/util-common';

import {
  PaginationWrapperDto,
  CodeableConceptDto,
  CodeableConceptValueDto,
  ComponentCodeErrorCode,
  ExtendedComponentFilter,
  ValidationResult,
} from '@treetop/shared/model';

import { LanguageService } from './language-service';
import { LoadingService } from './loading.service';
import { MessagesService } from './messages.service';

@Injectable({
  providedIn: 'root',
})
export class ValueSetHttpService {
  private http = inject(HttpClient);
  private loading = inject(LoadingService);
  private messages = inject(MessagesService);
  private languageService = inject(LanguageService);

  baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api`;

  loadCodeableConcepts(
    query: ExtendedComponentFilter = {},
    options: SearchOptions = { page: 1, itemsPerPage: 10, languageCode: 'de' },
  ): Observable<PaginationWrapperDto<CodeableConceptDto>> {
    const optionParams = objectToUrlParams(options);
    const queryParams = objectToUrlParams(query);

    const loadCodeableConcepts$ = this.http
      .get<PaginationWrapperDto<CodeableConceptDto>>(`${this.baseUrl}/codeable-concepts?${queryParams}&${optionParams}`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.messages.showError('CouldNotLoadValueSets');
          return throwError(() => error);
        }),
        shareReplay(),
      );
    this.loading.showLoaderUntilCompleted(loadCodeableConcepts$).subscribe();
    return loadCodeableConcepts$;
  }

  loadCodeableConceptsForComponent(
    componentCode: string,
    componentVersion: string,
    languageCode: string,
  ): Observable<CodeableConceptDto[]> {
    const codeableConceptsForComponent$ = this.http
      .get<
        CodeableConceptDto[]
      >(`${this.baseUrl}/codeable-concepts/for-component/${componentCode}/${componentVersion}?languageCode=${languageCode}`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.messages.showError('CouldNotLoadValueSetsForComponent');
          return throwError(() => error);
        }),
        shareReplay(),
      );
    this.loading.showLoaderUntilCompleted(codeableConceptsForComponent$).subscribe();
    return codeableConceptsForComponent$;
  }

  loadValues(
    codeableConceptCode: string,
    query: ExtendedComponentFilter = {},
    options: SearchOptions = { page: 1, itemsPerPage: 10, languageCode: 'de' },
  ): Observable<PaginationWrapperDto<CodeableConceptValueDto>> {
    const optionParams = objectToUrlParams(options);
    const queryParams = objectToUrlParams(query);
    const url = `${this.baseUrl}/codeable-concepts/values?codeableConceptCode=${codeableConceptCode}&${queryParams}&${optionParams}`;
    const loadValues$ = this.http.get(url).pipe(
      catchError((error: HttpErrorResponse) => {
        this.messages.showError('CouldNotLoadCodeableConcepts');
        return throwError(() => error);
      }),
      shareReplay(),
    );
    return loadValues$;
  }

  addCodeableConcept(changes: Partial<CodeableConceptDto>): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/codeable-concepts`, changes).pipe(
      catchError((error: HttpErrorResponse) => {
        this.messages.showError('DefinitionSaveError');
        return throwError(() => error);
      }),
      tap(() => this.messages.showInfo('DefinitionSaved')),
      shareReplay(),
    );
  }

  updateCodeableConcept(changes: Partial<CodeableConceptDto>): Observable<unknown> {
    return this.http.put(`${this.baseUrl}/codeable-concepts/${changes.code}`, changes).pipe(
      catchError((error: HttpErrorResponse) => {
        this.messages.showError('CodeableConceptSaveError');
        return throwError(() => error);
      }),
      tap(() => this.messages.showInfo('ValueSetSaved')),
      shareReplay(),
    );
  }

  validateCodeableConceptCode(code: string) {
    const validationUrl = `${this.baseUrl}/codeable-concepts/${code}/validate`;
    const existsUrl = `${this.baseUrl}/codeable-concepts/${code}/exists`;
    const exists = this.http.get<boolean>(existsUrl);
    const valid = this.http.get<ValidationResult<ComponentCodeErrorCode>>(validationUrl);
    return forkJoin([exists, valid]).pipe(
      catchError((error: HttpErrorResponse) => {
        this.messages.showError('ErrorValidatingTreetopcode');
        return throwError(() => error);
      }),
      shareReplay(),
    );
  }

  validateCodeableConceptValueCode(code: string) {
    const validationUrl = `${this.baseUrl}/codeable-concepts/values/${code}/validate`;
    const existsUrl = `${this.baseUrl}/codeable-concepts/values/${code}/exists`;
    const exists = this.http.get<boolean>(existsUrl);
    const valid = this.http.get<ValidationResult<ComponentCodeErrorCode>>(validationUrl);
    return forkJoin([exists, valid]).pipe(
      catchError((error: HttpErrorResponse) => {
        this.messages.showError('ErrorValidatingTreetopcode');
        return throwError(() => error);
      }),
      shareReplay(),
    );
  }

  deleteCodeableConcept(code: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/codeable-concepts/${code}`).pipe(
      catchError((error: HttpErrorResponse) => {
        this.messages.showError('CouldNotDeleteComponent');
        return throwError(() => error);
      }),
      tap(() => this.messages.showInfo('ComponentSuccessfullyDeleted')),
      shareReplay(),
    );
  }

  loadCodeableConcept(code: string): Observable<CodeableConceptDto> {
    const langCode = this.languageService.language.state;
    return this.http.get<CodeableConceptDto>(`${this.baseUrl}/codeable-concepts/${code}?languageCode=${langCode}`).pipe(
      map((result) => {
        return result;
      }),
      catchError((error: HttpErrorResponse) => {
        this.messages.showError('CouldNotLoadCodeableConcept');
        return throwError(() => error);
      }),
      shareReplay(),
    );
  }
}
